import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useGuild } from '../contexts/GuildContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
    FileText, 
    Download,
    Clock,
    User,
    Tag,
    ChevronLeft,
    ChevronRight,
    Eye
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Transcripts() {
    const { token } = useAuth();
    const { selectedGuild } = useGuild();
    const [transcripts, setTranscripts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedTranscript, setSelectedTranscript] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);

    const fetchTranscripts = async () => {
        if (!selectedGuild || !token) return;
        
        setLoading(true);
        try {
            const response = await axios.get(`${API}/guilds/${selectedGuild}/transcripts?page=${page}&limit=10`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTranscripts(response.data.transcripts || []);
            setTotalPages(response.data.pages || 1);
        } catch (error) {
            console.error('Error fetching transcripts:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTranscripts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGuild, token, page]);

    const viewTranscript = (transcript) => {
        setSelectedTranscript(transcript);
        setViewDialogOpen(true);
    };

    const downloadTranscript = (transcript) => {
        const content = transcript.messages?.map(msg => 
            `[${new Date(msg.created_at).toLocaleString()}] ${msg.username}: ${msg.content}`
        ).join('\n') || 'No messages';
        
        const blob = new Blob([
            `Ticket: ${transcript.subject}\n`,
            `Created by: ${transcript.username}\n`,
            `Category: ${transcript.category}\n`,
            `Priority: ${transcript.priority}\n`,
            `Created: ${new Date(transcript.created_at).toLocaleString()}\n`,
            `Closed: ${transcript.closed_at ? new Date(transcript.closed_at).toLocaleString() : 'N/A'}\n`,
            `\n--- Messages ---\n\n`,
            content
        ], { type: 'text/plain' });
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ticket-${transcript.id.slice(0, 8)}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading && transcripts.length === 0) {
        return (
            <div className="space-y-6" data-testid="transcripts-loading">
                <Skeleton className="h-10 w-48" />
                {[1, 2, 3, 4, 5].map(i => (
                    <Skeleton key={i} className="h-24" />
                ))}
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="transcripts-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold">Transcripts</h1>
                <p className="text-muted-foreground">View and download closed ticket transcripts</p>
            </div>

            {/* Transcripts List */}
            {transcripts.length > 0 ? (
                <div className="space-y-3">
                    {transcripts.map((transcript, index) => (
                        <motion.div
                            key={transcript.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                        >
                            <Card className="card-interactive" data-testid={`transcript-${transcript.id}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold truncate">{transcript.subject}</h3>
                                            
                                            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                                                <span className="flex items-center gap-1">
                                                    <User className="w-3 h-3" />
                                                    {transcript.username}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Tag className="w-3 h-3" />
                                                    {transcript.category}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    Closed: {transcript.closed_at ? new Date(transcript.closed_at).toLocaleDateString() : 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline">
                                                {transcript.messages?.length || 0} messages
                                            </Badge>
                                            
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => viewTranscript(transcript)}
                                                data-testid={`view-transcript-${transcript.id}`}
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                            
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => downloadTranscript(transcript)}
                                                data-testid={`download-transcript-${transcript.id}`}
                                            >
                                                <Download className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            ) : (
                <Card>
                    <CardContent className="py-12 text-center">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <h3 className="text-lg font-semibold mb-1">No transcripts yet</h3>
                        <p className="text-muted-foreground">
                            Closed tickets will appear here as transcripts
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    <span className="text-sm text-muted-foreground">
                        Page {page} of {totalPages}
                    </span>
                    
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                    >
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>
            )}

            {/* View Transcript Dialog */}
            <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle>{selectedTranscript?.subject}</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-4 text-sm">
                            <Badge variant="outline">{selectedTranscript?.category}</Badge>
                            <span className="text-muted-foreground">
                                Created by: {selectedTranscript?.username}
                            </span>
                            <span className="text-muted-foreground">
                                {selectedTranscript?.messages?.length || 0} messages
                            </span>
                        </div>
                        
                        <ScrollArea className="h-[400px] border rounded-lg p-4">
                            <div className="space-y-4">
                                {selectedTranscript?.messages?.map((msg, index) => (
                                    <div key={msg.id || index} className={`flex gap-3 ${msg.is_staff ? 'flex-row-reverse' : ''}`}>
                                        <div className={`flex-1 ${msg.is_staff ? 'text-right' : ''}`}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-sm font-medium">{msg.username}</span>
                                                {msg.is_staff && (
                                                    <Badge variant="outline" className="text-xs">Staff</Badge>
                                                )}
                                                <span className="text-xs text-muted-foreground">
                                                    {new Date(msg.created_at).toLocaleString()}
                                                </span>
                                            </div>
                                            <div className={`rounded-lg p-3 ${msg.is_staff ? 'bg-primary/10 ml-auto' : 'bg-muted'} max-w-[80%] ${msg.is_staff ? 'ml-auto' : ''}`}>
                                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                
                                {(!selectedTranscript?.messages || selectedTranscript.messages.length === 0) && (
                                    <div className="text-center py-8 text-muted-foreground">
                                        No messages in this transcript
                                    </div>
                                )}
                            </div>
                        </ScrollArea>
                        
                        <div className="flex justify-end">
                            <Button onClick={() => downloadTranscript(selectedTranscript)}>
                                <Download className="w-4 h-4 mr-2" />
                                Download
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
